import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import AdminAppReviewService from "../../review/AdminAppReviewService";

const MAX_JOURNEY_LEVEL = 1100;

/** Milestone levels that grant (or end) the journey — presets jump to level − 1. */
const MILESTONE_DEFS = (() => {
    const list = [];
    for (let level = 10; level < MAX_JOURNEY_LEVEL; level += 10) {
        if (level % 100 === 0) {
            list.push({
                level,
                name: "Streak Sparks",
                bonusLabel: "+10 coins",
                kind: "streak",
            });
        } else {
            list.push({
                level,
                name: "Brain Power",
                bonusLabel: "+5 coins",
                kind: "brain",
            });
        }
    }
    list.push({
        level: MAX_JOURNEY_LEVEL,
        name: "Word Master",
        bonusLabel: "none",
        kind: "master",
    });
    return list;
})();

/** Compact preset buttons: start + selected milestone−1 values for testing. */
const PRESET_MILESTONE_LEVELS = [10, 20, 30, 50, 100, 200, 300, 500, 1000, 1100];

const LEVEL_PRESETS = [
    { level: 1, label: "1", hint: "Start" },
    ...PRESET_MILESTONE_LEVELS.map((milestone) => {
        const def = MILESTONE_DEFS.find((m) => m.level === milestone);
        return {
            level: milestone - 1,
            label: String(milestone - 1),
            hint: def ? `→ ${milestone} ${def.name}` : `→ ${milestone}`,
        };
    }),
];

const RULE_ROWS = [
    {
        when: "Every 10 (10, 20, 30… — not 100s)",
        name: "Brain Power",
        bonus: "+5 coins",
    },
    {
        when: "Every 100 (100, 200, 300…)",
        name: "Streak Sparks",
        bonus: "+10 coins",
    },
    {
        when: "1100",
        name: "Word Master (journey end)",
        bonus: "none",
    },
];

function parseJourneyPayload(data) {
    if (
        data == null
        || (typeof data.status === "number" && data.status >= 400)
        || (data.error && !Array.isArray(data.seasons))
    ) {
        const msg =
            data?.message
            || data?.error
            || (data?.status === 404
                ? "Journey API not found — restart puzzle-be"
                : "Failed to load Word Wheel level");
        return { error: msg, data: null };
    }
    return { error: null, data };
}

function resolveLevel(journey) {
    if (!journey) return null;
    const seasons = Array.isArray(journey.seasons) ? journey.seasons : [];
    const defaultSeason = journey.defaultSeason || "season_2026";
    const top = Number(journey.level);
    if (Number.isFinite(top) && top > 0) return top;
    const defaultRow = seasons.find(
        (s) => String(s?.season || "").trim().toLowerCase() === String(defaultSeason).toLowerCase(),
    );
    const fromDefault = Number(defaultRow?.level);
    if (Number.isFinite(fromDefault) && fromDefault > 0) return fromDefault;
    let max = null;
    for (const row of seasons) {
        const n = Number(row?.level);
        if (Number.isFinite(n) && n > 0 && (max == null || n > max)) max = n;
    }
    return max;
}

function kindChipColor(kind) {
    if (kind === "streak") return "warning";
    if (kind === "master") return "success";
    return "info";
}

/**
 * Admin user Word Wheel tab — view and set main-journey level for testing.
 */
export default function WordWheelAdmin({ cloudUserId }) {
    const [journey, setJourney] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [levelDraft, setLevelDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [review, setReview] = useState(null);
    const [removingReview, setRemovingReview] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const showSnack = useCallback((severity, message) => {
        setSnackbar({ open: true, severity, message });
    }, []);

    const load = useCallback(async () => {
        if (!cloudUserId) return;
        setLoading(true);
        setError(null);
        try {
            const raw = await ApiService.getData(`/v1/stats/user/${cloudUserId}/word-wheel-journey`);
            const { error: err, data } = parseJourneyPayload(raw);
            if (err) {
                setError(err);
                setJourney(null);
            } else {
                setJourney(data);
                const level = resolveLevel(data);
                setLevelDraft(level != null ? String(level) : "1");
            }
            const reviewResult = await AdminAppReviewService.forUser(cloudUserId, "word_wheel_quest");
            setReview(reviewResult.success ? reviewResult.data?.review || null : null);
        } catch (err) {
            Logger.error("WordWheelAdmin load:", err);
            setError(err?.message || "Failed to load Word Wheel level");
            setJourney(null);
        } finally {
            setLoading(false);
        }
    }, [cloudUserId]);

    useEffect(() => {
        load();
    }, [load]);

    const currentLevel = resolveLevel(journey);
    const maxLevel = Number(journey?.maxLevel) || MAX_JOURNEY_LEVEL;
    const minLevel = Number(journey?.minLevel) || 1;
    const defaultSeason = journey?.defaultSeason || "season_2026";
    const seasons = Array.isArray(journey?.seasons) ? journey.seasons : [];
    const packs = Array.isArray(journey?.packs) ? journey.packs : [];
    const defaultRow = seasons.find(
        (s) => String(s?.season || "").trim().toLowerCase() === String(defaultSeason).toLowerCase(),
    );
    const updatedAt =
        defaultRow?.updatedAt != null ? UtilService.formatDate(defaultRow.updatedAt) || null : null;

    const nearbyMilestones = useMemo(() => {
        const level = currentLevel != null ? currentLevel : 1;
        const upcoming = MILESTONE_DEFS.filter((m) => m.level > level).slice(0, 8);
        if (upcoming.length > 0) return upcoming;
        return MILESTONE_DEFS.slice(-5);
    }, [currentLevel]);

    const handleSave = async (explicitLevel) => {
        if (!cloudUserId) return;
        const level = Number(explicitLevel != null ? explicitLevel : levelDraft);
        if (!Number.isInteger(level) || level < minLevel || level > maxLevel) {
            showSnack("warning", `Level must be an integer from ${minLevel} to ${maxLevel}`);
            return;
        }
        if (currentLevel != null && level === currentLevel) {
            showSnack("info", `Already at level ${level}`);
            return;
        }

        setSaving(true);
        try {
            const raw = await ApiService.putData(`/v1/stats/user/${cloudUserId}/word-wheel-journey`, {
                level,
                season: defaultSeason,
            });
            const { error: err, data } = parseJourneyPayload(raw);
            if (err) {
                showSnack("error", err);
                return;
            }
            setJourney(data);
            const next = resolveLevel(data);
            setLevelDraft(next != null ? String(next) : String(level));
            const removed = Number(data?.removedPlays) || 0;
            const applied = next ?? level;
            showSnack(
                "success",
                removed > 0
                    ? `Word Wheel level set to ${applied}. Removed ${removed} play record${removed === 1 ? "" : "s"} above level ${applied}.`
                    : `Word Wheel level set to ${applied}`,
            );
        } catch (err) {
            Logger.error("WordWheelAdmin set level:", err);
            showSnack("error", err?.message || "Failed to set Word Wheel level");
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveReview = async () => {
        if (!review?.id) return;
        setRemovingReview(true);
        const result = await AdminAppReviewService.remove(review.id);
        setRemovingReview(false);
        if (result.success) {
            setReview(null);
            showSnack("success", "Review removed");
            return;
        }
        showSnack("error", result.error || "Failed to remove review");
    };

    if (!cloudUserId) {
        return (
            <Alert severity="warning">Cloud user id is required to load Word Wheel journey.</Alert>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.2fr) minmax(280px, 0.8fr)" },
                    gap: 2,
                    alignItems: "start",
                    mb: 3,
                }}
            >
                <Card>
                    <CardContent>
                        <Typography variant="h5" gutterBottom>
                            Word Wheel Quest
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Main journey level for testing. Presets jump to <strong>milestone − 1</strong> so
                            the next clear awards that milestone.
                        </Typography>

                        {error ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        ) : null}

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 3,
                                alignItems: "center",
                                p: 2.5,
                                mb: 3,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                            }}
                        >
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Current level
                                </Typography>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: { xs: "2.75rem", sm: "3.25rem" },
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        color: "primary.main",
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    {currentLevel != null ? currentLevel : "—"}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                <Chip size="small" label={defaultSeason} variant="outlined" />
                                <Typography variant="body2" color="text.secondary">
                                    of {maxLevel} max
                                </Typography>
                                {updatedAt ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Updated {updatedAt}
                                    </Typography>
                                ) : null}
                                {currentLevel == null ? (
                                    <Typography variant="caption" color="text.secondary">
                                        No journey row yet — saving will create one.
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                gap: 2,
                                alignItems: "start",
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Set level
                                </Typography>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems={{ sm: "center" }}
                                    sx={{ mb: 2 }}
                                >
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Level"
                                        value={levelDraft}
                                        onChange={(e) => setLevelDraft(e.target.value)}
                                        disabled={saving}
                                        slotProps={{
                                            htmlInput: { min: minLevel, max: maxLevel, step: 1 },
                                        }}
                                        sx={{ width: { xs: "100%", sm: 160 } }}
                                    />
                                    <Button variant="contained" onClick={() => handleSave()} disabled={saving}>
                                        {saving ? "Saving…" : "Apply level"}
                                    </Button>
                                </Stack>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Quick presets (milestone − 1)
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                                    {LEVEL_PRESETS.filter((p) => p.level >= minLevel && p.level <= maxLevel).map(
                                        (p) => (
                                            <Button
                                                key={p.level}
                                                size="small"
                                                variant={currentLevel === p.level ? "contained" : "outlined"}
                                                disabled={saving}
                                                title={p.hint}
                                                onClick={() => {
                                                    setLevelDraft(String(p.level));
                                                    handleSave(p.level);
                                                }}
                                                sx={{ textTransform: "none" }}
                                            >
                                                {p.label}
                                                {p.hint && p.level !== 1 ? (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        sx={{ ml: 0.75, opacity: 0.8 }}
                                                    >
                                                        {p.hint}
                                                    </Typography>
                                                ) : null}
                                            </Button>
                                        ),
                                    )}
                                </Stack>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    minHeight: 140,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Review
                                </Typography>
                                {review ? (
                                    <Stack spacing={1} alignItems="flex-start">
                                        <Typography variant="body2">
                                            {review.platform === "ios"
                                                ? "iOS"
                                                : review.platform === "android"
                                                  ? "Android"
                                                  : review.platform || "—"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {Number(review.promptedAt) > 0
                                                ? new Date(Number(review.promptedAt)).toLocaleString()
                                                : "—"}
                                        </Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            disabled={removingReview}
                                            onClick={handleRemoveReview}
                                        >
                                            {removingReview ? "Removing…" : "Remove review"}
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No review
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Stack spacing={2}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Milestones
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell>Reward</TableCell>
                                            <TableCell align="right">Coins</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {RULE_ROWS.map((row) => (
                                            <TableRow key={row.name}>
                                                <TableCell sx={{ verticalAlign: "top" }}>
                                                    <Typography variant="body2">{row.when}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {row.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2">{row.bonus}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Upcoming (set to −1)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                                Jump to the level just before the milestone, then clear that puzzle to earn it.
                            </Typography>
                            <Stack spacing={1}>
                                {nearbyMilestones.map((m) => {
                                    const preset = m.level - 1;
                                    return (
                                        <Box
                                            key={m.level}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                label={m.name}
                                                color={kindChipColor(m.kind)}
                                                variant="outlined"
                                            />
                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                                                L{m.level} · {m.bonusLabel}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant={currentLevel === preset ? "contained" : "outlined"}
                                                disabled={saving || preset < minLevel}
                                                onClick={() => {
                                                    setLevelDraft(String(preset));
                                                    handleSave(preset);
                                                }}
                                            >
                                                Set {preset}
                                            </Button>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Box>

            {packs.length > 0 ? (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Packs
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Store packs on this account, including a purchase made on a device already registered here.
                        </Typography>
                        <Stack spacing={1}>
                            {packs.map((pack) => (
                                <Box
                                    key={pack.code}
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        py: 0.75,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={pack.label || pack.code}
                                        color={pack.owned ? "success" : "default"}
                                        variant={pack.owned ? "filled" : "outlined"}
                                    />
                                    <Typography variant="body2" fontWeight={700}>
                                        {pack.owned ? "Purchased" : "Not purchased"}
                                    </Typography>
                                    {pack.owned && pack.productId ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {pack.productId}
                                        </Typography>
                                    ) : null}
                                    {pack.owned && pack.purchaseDate != null ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {UtilService.formatDate(pack.purchaseDate) || ""}
                                        </Typography>
                                    ) : null}
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {seasons.length > 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Seasons
                        </Typography>
                        <Stack spacing={1}>
                            {seasons.map((row) => (
                                <Box
                                    key={row.season}
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        py: 0.75,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={row.season}
                                        color={
                                            String(row.season).toLowerCase() ===
                                            String(defaultSeason).toLowerCase()
                                                ? "primary"
                                                : "default"
                                        }
                                        variant="outlined"
                                    />
                                    <Typography variant="body2" fontWeight={700}>
                                        Level {row.level}
                                    </Typography>
                                    {row.updatedAt != null ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Updated {UtilService.formatDate(row.updatedAt) || "—"}
                                        </Typography>
                                    ) : null}
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((prev) => ({ ...prev, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
